import { useMemo, useState } from 'react';
import { Box, ButtonBase, Menu, MenuItem, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface AutoRefreshProps {
  onIntervalChange: (minutes: number | null) => void;
  currentInterval: number | null;
}

const AUTO_REFRESH_OPTIONS = [
  { label: 'Off', value: 0 },
  { label: 'Every 5 mins', value: 5 },
  { label: 'Every 10 mins', value: 10 },
  { label: 'Every 15 mins', value: 15 },
];

const AutoRefresh = ({ onIntervalChange, currentInterval }: AutoRefreshProps) => {
  const theme = useTheme();
  const p = theme.palette;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const selectedLabel = useMemo(
    () => AUTO_REFRESH_OPTIONS.find((opt) => opt.value === (currentInterval ?? 0))?.label ?? 'Off',
    [currentInterval],
  );

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, height: 24, m: 0, p: 0, lineHeight: 1 }}>
      <Typography variant="body2" sx={{ color: p.textLight.secondary, lineHeight: 1 }}>
        Auto-Refresh:
      </Typography>
      <ButtonBase
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label="Auto Refresh Interval"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.125,
          height: 24,
          m: 0,
          p: 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: p.textLight.dark,
            lineHeight: 1,
            mr: 0,
          }}
        >
          {selectedLabel}
        </Typography>
        <ExpandMoreIcon sx={{ color: p.textLight.secondary, fontSize: 18, ml: -0.25 }} />
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: `${p.backgroundLight.secondary} !important`,
              backgroundImage: 'none !important',
              color: `${p.textLight.dark} !important`,
              '& .MuiMenuItem-root': {
                color: `${p.textLight.dark} !important`,
              },
            },
          },
        }}
        MenuListProps={{
          sx: {
            backgroundColor: `${p.backgroundLight.secondary} !important`,
            color: `${p.textLight.dark} !important`,
          },
        }}
      >
        {AUTO_REFRESH_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.value}
            selected={opt.value === (currentInterval ?? 0)}
            onClick={() => {
              onIntervalChange(opt.value === 0 ? null : opt.value);
              setAnchorEl(null);
            }}
            sx={{
              color: p.textLight.dark,
              '&.Mui-selected': {
                backgroundColor: p.backgroundLight.primary,
              },
              '&.Mui-selected:hover': {
                backgroundColor: p.backgroundLight.primary,
              },
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default AutoRefresh;
